import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ToastProvider";
import { LedgerAccount, LedgerEntryInput, LedgerJournal, LedgerWorksheetEntry } from "@/types/ledger";
import { fetchAccountWorksheet, updateLedgerLine, suggestLedgerCategory } from "@/api/ledger";
import { getBankTransactions } from "@/api/bank";
import type { BankTransaction } from "@/types/bank";

type DrawerProps = {
  account: LedgerAccount | null;
  open: boolean;
  onClose: () => void;
  accounts: LedgerAccount[];
  journals: LedgerJournal[];
  onUpdated: () => Promise<void> | void;
  createEntry: (payload: LedgerEntryInput) => Promise<void>;
};

type RowDraft = {
  memo: string;
  category: string;
  counter_account_id: number | null;
  debit: string;
  credit: string;
  attachment_urls: string[];
  date: string;
};

type NewEntryDraft = {
  date: string;
  journal_id: number | "";
  description: string;
  category: string;
  counter_account_id: number | "";
  amount: string;
  debitSide: "debit" | "credit";
  attachments: string[];
};

const DEFAULT_NEW_ENTRY: NewEntryDraft = {
  date: format(new Date(), "yyyy-MM-dd"),
  journal_id: "",
  description: "",
  category: "",
  counter_account_id: "",
  amount: "",
  debitSide: "debit",
  attachments: [],
};

export function AccountWorksheetDrawer({
  account,
  open,
  onClose,
  accounts,
  journals,
  onUpdated,
  createEntry,
}: DrawerProps) {
  const { push } = useToast();
  const [rows, setRows] = useState<Record<number, { source: LedgerWorksheetEntry; draft: RowDraft }>>({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [newEntry, setNewEntry] = useState<NewEntryDraft>(DEFAULT_NEW_ENTRY);
  const [newEntrySaving, setNewEntrySaving] = useState(false);
  const [reconcileMode, setReconcileMode] = useState(false);
  const [bankLines, setBankLines] = useState<BankTransaction[]>([]);
  const [attachmentPrompt, setAttachmentPrompt] = useState<{ lineId: number; value: string }>({ lineId: 0, value: "" });

  const loadWorksheet = useCallback(async () => {
    if (!account || !open) return;
    setLoading(true);
    try {
      const payload = await fetchAccountWorksheet(account.id);
      const nextRows: typeof rows = {};
      payload.forEach((row) => {
        nextRows[row.line_id] = {
          source: row,
          draft: {
            memo: row.description ?? "",
            category: row.category ?? "",
            counter_account_id: row.counter_account_id ?? null,
            debit: String(row.debit ?? ""),
            credit: String(row.credit ?? ""),
            attachment_urls: row.attachment_urls ?? [],
            date: row.date ? row.date.slice(0, 10) : format(new Date(), "yyyy-MM-dd"),
          },
        };
      });
      setRows(nextRows);
    } catch (err) {
      push({
        title: "Worksheet failed",
        message: err instanceof Error ? err.message : "Unable to load worksheet.",
      });
    } finally {
      setLoading(false);
    }
  }, [account, open, push]);

  useEffect(() => {
    void loadWorksheet();
  }, [loadWorksheet]);

  useEffect(() => {
    if (!reconcileMode) {
      setBankLines([]);
      return;
    }
    const run = async () => {
      try {
        const transactions = await getBankTransactions({ status: "unmatched", limit: 10 });
        setBankLines(transactions);
      } catch {
        setBankLines([]);
      }
    };
    void run();
  }, [reconcileMode]);

  const handleFieldChange = (lineId: number, field: keyof RowDraft, value: string | number | string[]) => {
    setRows((prev) => {
      const target = prev[lineId];
      if (!target) return prev;
      const draft = { ...target.draft };
      if (field === "counter_account_id") {
        draft.counter_account_id = value ? Number(value) : null;
      } else if (field === "attachment_urls" && Array.isArray(value)) {
        draft.attachment_urls = value;
      } else {
        (draft as any)[field] = value;
      }
      return {
        ...prev,
        [lineId]: { ...target, draft },
      };
    });
  };

  const resetRow = (lineId: number) => {
    setRows((prev) => {
      const target = prev[lineId];
      if (!target) return prev;
      return {
        ...prev,
        [lineId]: {
          source: target.source,
          draft: {
            memo: target.source.description ?? "",
            category: target.source.category ?? "",
            counter_account_id: target.source.counter_account_id ?? null,
            debit: String(target.source.debit ?? ""),
            credit: String(target.source.credit ?? ""),
            attachment_urls: target.source.attachment_urls ?? [],
            date: target.source.date ? target.source.date.slice(0, 10) : format(new Date(), "yyyy-MM-dd"),
          },
        },
      };
    });
  };

  const diffRow = (lineId: number) => {
    const row = rows[lineId];
    if (!row) return {};
    const diff: Record<string, unknown> = {};
    if ((row.draft.memo ?? "") !== (row.source.description ?? "")) diff.memo = row.draft.memo;
    if ((row.draft.category ?? "") !== (row.source.category ?? "")) diff.category = row.draft.category;
    if ((row.draft.counter_account_id ?? null) !== (row.source.counter_account_id ?? null)) {
      diff.counter_account_id = row.draft.counter_account_id;
    }
    if (row.draft.debit !== String(row.source.debit ?? "")) diff.debit = row.draft.debit;
    if (row.draft.credit !== String(row.source.credit ?? "")) diff.credit = row.draft.credit;
    if ((row.draft.date ?? "") !== (row.source.date ?? "").slice(0, 10)) diff.date = row.draft.date;
    if (
      JSON.stringify(row.draft.attachment_urls ?? []) !== JSON.stringify(row.source.attachment_urls ?? [])
    ) {
      diff.attachment_urls = row.draft.attachment_urls;
    }
    return diff;
  };

  const handleSaveRow = async (lineId: number) => {
    const row = rows[lineId];
    if (!account || !row) return;
    const payload = diffRow(lineId);
    if (Object.keys(payload).length === 0) {
      push({ title: "No changes to save", message: "Update fields before saving." });
      return;
    }
    setSavingId(lineId);
    try {
      await updateLedgerLine(row.source.entry_id, lineId, {
        memo: payload.memo as string | undefined,
        category: payload.category as string | undefined,
        counter_account_id: payload.counter_account_id as number | undefined,
        debit: payload.debit as string | undefined,
        credit: payload.credit as string | undefined,
        attachment_urls: payload.attachment_urls as string[] | undefined,
        date: payload.date as string | undefined,
      });
      push({ title: "Entry updated", message: "Ledger line saved." });
      await loadWorksheet();
      await onUpdated();
    } catch (err) {
      push({
        title: "Update failed",
        message: err instanceof Error ? err.message : "Unable to update line.",
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleSuggestCategory = async (lineId: number) => {
    const row = rows[lineId];
    if (!row) return;
    try {
      const suggestion = await suggestLedgerCategory(row.source.entry_id, lineId);
      if (suggestion.category) {
        handleFieldChange(lineId, "category", suggestion.category);
      } else {
        push({ title: "No suggestion", message: "No similar entries found." });
      }
    } catch (err) {
      push({ title: "Suggestion failed", message: err instanceof Error ? err.message : "Unable to suggest." });
    }
  };

  const handleNewEntryChange = (field: keyof NewEntryDraft, value: string) => {
    setNewEntry((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateEntry = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!account) return;
    if (!newEntry.journal_id || !newEntry.counter_account_id || !newEntry.amount) {
      push({ title: "Missing data", message: "Select journal, counter account, and amount." });
      return;
    }
    const amount = Number(newEntry.amount);
    if (!amount || amount <= 0) {
      push({ title: "Amount required", message: "Enter a positive amount." });
      return;
    }
    const debitLine = newEntry.debitSide === "debit";
    const entryPayload: LedgerEntryInput = {
      date: newEntry.date,
      journal_id: Number(newEntry.journal_id),
      memo: newEntry.description,
      source: "worksheet",
      lines: [
        {
          account_id: account.id,
          debit: debitLine ? amount : 0,
          credit: debitLine ? 0 : amount,
          memo: newEntry.description,
          category: newEntry.category || undefined,
          attachment_urls: newEntry.attachments,
        },
        {
          account_id: Number(newEntry.counter_account_id),
          debit: debitLine ? 0 : amount,
          credit: debitLine ? amount : 0,
          memo: newEntry.description,
        },
      ],
    };
    setNewEntrySaving(true);
    try {
      await createEntry(entryPayload);
      push({ title: "Entry created", message: "New journal entry added." });
      setNewEntry(DEFAULT_NEW_ENTRY);
      await loadWorksheet();
      await onUpdated();
    } catch (err) {
      push({ title: "Create failed", message: err instanceof Error ? err.message : "Unable to create entry." });
    } finally {
      setNewEntrySaving(false);
    }
  };

  const handleAttachmentAdd = (lineId: number) => {
    const value = attachmentPrompt.value.trim();
    if (!value) return;
    const row = rows[lineId];
    if (!row) return;
    const next = [...row.draft.attachment_urls, value];
    handleFieldChange(lineId, "attachment_urls", next);
    setAttachmentPrompt({ lineId: 0, value: "" });
  };

  const sortedRows = useMemo(
    () =>
      Object.values(rows).sort((a, b) =>
        a.source.date.localeCompare(b.source.date) || a.source.line_id - b.source.line_id,
      ),
    [rows],
  );

  if (!open || !account) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/40 backdrop-blur-sm">
      <div className="h-full w-full max-w-5xl overflow-y-auto bg-background p-6 shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-border pb-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-primary">Worksheet</p>
            <h2 className="text-2xl font-semibold text-foreground">
              {account.code} · {account.name}
            </h2>
            <p className="text-sm text-muted-foreground">
              Balance: {Number(account.balance ?? 0).toLocaleString(undefined, { style: "currency", currency: "USD" })}
            </p>
          </div>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>

        <section className="mt-6 space-y-4 rounded-2xl border border-border bg-background/30 p-4">
          <header className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Journal entries</h3>
              <p className="text-xs text-muted-foreground">Edit lines inline. Debit and credit values are mirrored automatically.</p>
            </div>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                <input
                  type="checkbox"
                  checked={reconcileMode}
                  onChange={(event) => setReconcileMode(event.target.checked)}
                />
                Reconciliation mode
              </label>
              <Button variant="outline" size="sm" onClick={() => void loadWorksheet()} disabled={loading}>
                {loading ? "Refreshing…" : "Refresh"}
              </Button>
            </div>
          </header>

          {reconcileMode && (
            <div className="rounded-xl border border-warning/40 bg-warning/10 p-3 text-xs text-warning">
              <p className="font-semibold">Imported bank lines</p>
              {bankLines.length === 0 ? (
                <p className="mt-2 text-warning/80">No unmatched transactions available.</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {bankLines.map((line) => (
                    <li key={line.id} className="flex items-center justify-between gap-3 rounded border border-warning/30 px-3 py-2">
                      <span>
                        {line.description} ·{" "}
                        {Number(line.amount_signed).toLocaleString(undefined, { style: "currency", currency: "USD" })}
                      </span>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          push({
                            title: "Match placeholder",
                            message: "Future release will match to ledger entries.",
                          })
                        }
                      >
                        Match
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {sortedRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No ledger lines found for this account.</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="min-w-full text-sm">
                <thead className="bg-background/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-left">Description</th>
                    <th className="p-2 text-left">Category</th>
                    <th className="p-2 text-left">Counter Account</th>
                    <th className="p-2 text-right">Debit</th>
                    <th className="p-2 text-right">Credit</th>
                    <th className="p-2 text-right">Balance</th>
                    <th className="p-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map(({ source, draft }) => {
                    const canEdit = source.can_edit !== false;
                    const disabledClass = canEdit ? "" : "opacity-40 cursor-not-allowed";
                    return (
                      <tr key={source.line_id} className="border-t border-border/60">
                      <td className="p-2 align-top">
                        <input
                          type="date"
                          value={draft.date}
                          onChange={(event) => handleFieldChange(source.line_id, "date", event.target.value)}
                          className={`w-full rounded border border-border bg-transparent px-2 py-1 text-xs text-foreground ${disabledClass}`}
                          disabled={!canEdit}
                        />
                        <div className="text-[11px] text-muted-foreground">{source.journal}</div>
                      </td>
                      <td className="p-2 align-top">
                        <input
                          value={draft.memo}
                          onChange={(event) => handleFieldChange(source.line_id, "memo", event.target.value)}
                          className={`w-full rounded border border-border bg-transparent px-2 py-1 text-foreground ${disabledClass}`}
                          disabled={!canEdit}
                        />
                      </td>
                      <td className="p-2 align-top">
                        <div className="flex flex-col gap-1">
                          <input
                            value={draft.category}
                            onChange={(event) => handleFieldChange(source.line_id, "category", event.target.value)}
                            className={`w-full rounded border border-border bg-transparent px-2 py-1 text-foreground ${disabledClass}`}
                            disabled={!canEdit}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => void handleSuggestCategory(source.line_id)}
                            disabled={!canEdit}
                          >
                            AI Suggest
                          </Button>
                        </div>
                      </td>
                      <td className="p-2 align-top">
                        <select
                          value={draft.counter_account_id ?? ""}
                          onChange={(event) => handleFieldChange(source.line_id, "counter_account_id", event.target.value)}
                          className={`w-full rounded border border-border bg-transparent px-2 py-1 text-foreground ${disabledClass}`}
                          disabled={!canEdit}
                        >
                          <option value="">Auto</option>
                          {accounts
                            .filter((acct) => acct.id !== account.id)
                            .map((acct) => (
                              <option key={acct.id} value={acct.id}>
                                {acct.code} · {acct.name}
                              </option>
                            ))}
                        </select>
                      </td>
                      <td className="p-2 align-top">
                        <input
                          type="number"
                          value={draft.debit}
                          onChange={(event) => {
                            handleFieldChange(source.line_id, "debit", event.target.value);
                            if (Number(event.target.value) > 0) handleFieldChange(source.line_id, "credit", "0");
                          }}
                          className={`w-full rounded border border-border bg-transparent px-2 py-1 text-right text-foreground ${disabledClass}`}
                          disabled={!canEdit}
                        />
                      </td>
                      <td className="p-2 align-top">
                        <input
                          type="number"
                          value={draft.credit}
                          onChange={(event) => {
                            handleFieldChange(source.line_id, "credit", event.target.value);
                            if (Number(event.target.value) > 0) handleFieldChange(source.line_id, "debit", "0");
                          }}
                          className={`w-full rounded border border-border bg-transparent px-2 py-1 text-right text-foreground ${disabledClass}`}
                          disabled={!canEdit}
                        />
                      </td>
                      <td className="p-2 align-top text-right text-xs text-muted-foreground">
                        {Number(source.balance).toLocaleString(undefined, {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                      <td className="p-2 align-top text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={savingId === source.line_id || !canEdit}
                            onClick={() => void handleSaveRow(source.line_id)}
                          >
                            {savingId === source.line_id ? "Saving…" : "Save"}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={savingId === source.line_id || !canEdit}
                            onClick={() => resetRow(source.line_id)}
                          >
                            Reset
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setAttachmentPrompt({ lineId: source.line_id, value: "" })}
                            disabled={!canEdit}
                          >
                            Attachments ({draft.attachment_urls.length})
                          </Button>
                        </div>
                        {!canEdit && (
                          <p className="mt-2 text-[11px] text-muted-foreground">Use adjustments instead of editing this entry.</p>
                        )}
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="mt-6 space-y-3 rounded-2xl border border-border bg-background/30 p-4">
          <header>
            <h3 className="text-lg font-semibold text-foreground">Quick add entry</h3>
            <p className="text-xs text-muted-foreground">Adds a two-line journal entry that debits or credits this account.</p>
          </header>
          <form className="space-y-3" onSubmit={handleCreateEntry}>
            <div className="grid gap-3 md:grid-cols-3">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Date
                <input
                  type="date"
                  value={newEntry.date}
                  onChange={(event) => handleNewEntryChange("date", event.target.value)}
                  className="mt-1 w-full rounded border border-border bg-transparent px-3 py-2 text-foreground"
                />
              </label>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Journal
                <select
                  value={newEntry.journal_id}
                  onChange={(event) => handleNewEntryChange("journal_id", event.target.value)}
                  className="mt-1 w-full rounded border border-border bg-transparent px-3 py-2 text-foreground"
                >
                  <option value="">Select journal</option>
                  {journals.map((journal) => (
                    <option key={journal.id} value={journal.id}>
                      {journal.code} · {journal.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Amount
                <input
                  type="number"
                  value={newEntry.amount}
                  onChange={(event) => handleNewEntryChange("amount", event.target.value)}
                  className="mt-1 w-full rounded border border-border bg-transparent px-3 py-2 text-foreground"
                />
              </label>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Description
                <input
                  value={newEntry.description}
                  onChange={(event) => handleNewEntryChange("description", event.target.value)}
                  className="mt-1 w-full rounded border border-border bg-transparent px-3 py-2 text-foreground"
                />
              </label>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Category
                <input
                  value={newEntry.category}
                  onChange={(event) => handleNewEntryChange("category", event.target.value)}
                  className="mt-1 w-full rounded border border-border bg-transparent px-3 py-2 text-foreground"
                />
              </label>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Counter account
                <select
                  value={newEntry.counter_account_id}
                  onChange={(event) => handleNewEntryChange("counter_account_id", event.target.value)}
                  className="mt-1 w-full rounded border border-border bg-transparent px-3 py-2 text-foreground"
                >
                  <option value="">Select account</option>
                  {accounts
                    .filter((acct) => acct.id !== account.id)
                    .map((acct) => (
                      <option key={acct.id} value={acct.id}>
                        {acct.code} · {acct.name}
                      </option>
                    ))}
                </select>
              </label>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Debit or credit
                <select
                  value={newEntry.debitSide}
                  onChange={(event) =>
                    setNewEntry((prev) => ({ ...prev, debitSide: event.target.value as "debit" | "credit" }))
                  }
                  className="mt-1 w-full rounded border border-border bg-transparent px-3 py-2 text-foreground"
                >
                  <option value="debit">Debit this account</option>
                  <option value="credit">Credit this account</option>
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="submit" disabled={newEntrySaving}>
                {newEntrySaving ? "Saving…" : "Add entry"}
              </Button>
            </div>
          </form>
        </section>

        {attachmentPrompt.lineId > 0 && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="w-full max-w-md rounded-2xl border border-border bg-background p-4">
              <h4 className="text-lg font-semibold text-foreground">Add attachment URL</h4>
              <input
                value={attachmentPrompt.value}
                onChange={(event) => setAttachmentPrompt((prev) => ({ ...prev, value: event.target.value }))}
                className="mt-3 w-full rounded border border-border bg-transparent px-3 py-2 text-foreground"
                placeholder="https://..."
              />
              <div className="mt-3 flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setAttachmentPrompt({ lineId: 0, value: "" })}>
                  Cancel
                </Button>
                <Button onClick={() => handleAttachmentAdd(attachmentPrompt.lineId)}>Add</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
